import { useSearchParams } from 'react-router-dom';
import { parseIsoDate } from '@/lib/timezone';
import {
  AGING_BUCKETS,
  INVOICE_STATUSES,
  OUTSTANDING,
  type InvoiceFilters,
  type InvoiceSortColumn,
} from './useInvoices';

/**
 * The invoice list's filters, kept in the URL (M2-12 review, Kyle
 * 2026-09-28) like every other filtered screen here (`useJobListFilters`,
 * Customers, Dispatch): browser Back from an invoice restores the list as it
 * was, and the invoice page's "← All invoices" link carries the same search
 * string back.
 *
 * ⚠️ Every value is WHITELISTED, never cast: a hand-edited or stale URL falls
 * back to the default for that one filter rather than reaching the API as
 * something it refuses (or, for `status`, as a number the server might once
 * have read as a different status — pass A's `TryParse` lesson).
 *
 * A bare `/invoices` is the default view: Outstanding, newest number first.
 * "All statuses" therefore needs a value of its own, `status=all`.
 */

const PARAM = {
  status: 'status',
  customerId: 'customer',
  sentFrom: 'from',
  sentTo: 'to',
  aging: 'aging',
  sortBy: 'sort',
  sortDirection: 'dir',
} as const;

/** `status=all` — every status, the API's own default (no `status` sent). */
const ALL_STATUSES = 'all';

const SORTABLE: readonly InvoiceSortColumn[] = ['number', 'age', 'total', 'balanceDue'];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const DEFAULT_INVOICE_FILTERS: InvoiceFilters = {
  status: OUTSTANDING,
  customerId: '',
  sentFrom: '',
  sentTo: '',
  aging: '',
  sortBy: 'number',
  sortDirection: 'desc',
};

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | undefined =>
  allowed.find((a) => a === value);

const isoDate = (value: string | null) => (value !== null && parseIsoDate(value) ? value : '');

export function parseInvoiceFilters(params: URLSearchParams): InvoiceFilters {
  const status = params.get(PARAM.status);
  const customerId = params.get(PARAM.customerId);
  return {
    status: status === ALL_STATUSES ? '' : (oneOf(status, INVOICE_STATUSES) ?? OUTSTANDING),
    customerId: customerId !== null && UUID.test(customerId) ? customerId : '',
    sentFrom: isoDate(params.get(PARAM.sentFrom)),
    sentTo: isoDate(params.get(PARAM.sentTo)),
    aging: oneOf(params.get(PARAM.aging), AGING_BUCKETS) ?? '',
    sortBy: oneOf(params.get(PARAM.sortBy), SORTABLE) ?? 'number',
    sortDirection: params.get(PARAM.sortDirection) === 'asc' ? 'asc' : 'desc',
  };
}

/** Only what differs from the default, so the default view is a bare `/invoices`. */
export function writeInvoiceFilters(filters: InvoiceFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.status === '') next.set(PARAM.status, ALL_STATUSES);
  else if (filters.status !== OUTSTANDING) next.set(PARAM.status, filters.status);
  if (filters.customerId) next.set(PARAM.customerId, filters.customerId);
  if (filters.sentFrom) next.set(PARAM.sentFrom, filters.sentFrom);
  if (filters.sentTo) next.set(PARAM.sentTo, filters.sentTo);
  if (filters.aging) next.set(PARAM.aging, filters.aging);
  if (filters.sortBy !== 'number') next.set(PARAM.sortBy, filters.sortBy);
  if (filters.sortDirection !== 'desc') next.set(PARAM.sortDirection, filters.sortDirection);
  return next;
}

export function useInvoiceListFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseInvoiceFilters(searchParams);

  /**
   * Merged into the URL as THIS render saw it. ⚠️ react-router's updater form
   * hands over the searchParams of the render that created the setter, not
   * the live URL — so a caller writing later (the date boxes' settle timer)
   * must call the latest render's setter, which `Invoices` keeps in a ref.
   */
  function setFilters(patch: Partial<InvoiceFilters>) {
    setSearchParams((current) => {
      const now = parseInvoiceFilters(current);
      const next = { ...now, ...patch };
      return writeInvoiceFilters(next);
    });
  }

  return { filters, setFilters };
}
